import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { MetagameRow, MetagameWindow } from "@liga/shared";
import { api } from "../api/client";
import { trackEvent } from "../analytics";
import { ErrorBox, Loading } from "../components/Loading";

type WindowSelection =
  | { kind: "relative"; window: MetagameWindow }
  | { kind: "year"; year: number };

const RELATIVE_WINDOWS: Array<{ value: MetagameWindow; label: string }> = [
  { value: "30d", label: "30 dias" },
  { value: "90d", label: "90 dias" },
  { value: "365d", label: "365 dias" },
];

function formatPct(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

export function MetagamePage() {
  const [selection, setSelection] = useState<WindowSelection>({
    kind: "relative",
    window: "30d",
  });

  const yearsQuery = useQuery({
    queryKey: ["leagueYears"],
    queryFn: api.leagueYears,
  });

  const metagameQuery = useQuery({
    queryKey: ["metagame", selection],
    queryFn: () =>
      selection.kind === "relative"
        ? api.metagame({ window: selection.window })
        : api.metagame({ window: "year", year: selection.year }),
  });

  const years = yearsQuery.data ?? [];
  const data = metagameQuery.data;

  const summary = useMemo(() => {
    if (!data) return null;
    if (selection.kind === "year") return `Ano ${selection.year}`;
    const days = selection.window;
    return `Últimos ${days === "30d" ? "30 dias" : days === "90d" ? "90 dias" : "365 dias"}`;
  }, [data, selection]);

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-5">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#8a8170] font-bold mb-1.5">
            Cenário
          </div>
          <h1 className="font-display text-[44px] leading-none font-bold tracking-tight text-ink">
            Metagame
          </h1>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 bg-white border border-line rounded-xl p-1.5">
          {RELATIVE_WINDOWS.map((w) => {
            const active =
              selection.kind === "relative" && selection.window === w.value;
            return (
              <button
                key={w.value}
                type="button"
                onClick={() => {
                  setSelection({ kind: "relative", window: w.value });
                  trackEvent("metagame_window_change", {
                    kind: "relative",
                    value: w.value,
                  });
                }}
                className={[
                  "px-4 py-2 rounded-lg text-sm font-semibold transition-colors",
                  active
                    ? "bg-brand-700 text-white"
                    : "text-[#6b6356] hover:bg-[#f3efe6]",
                ].join(" ")}
              >
                {w.label}
              </button>
            );
          })}
          {years.length > 0 && (
            <span className="w-px h-6 bg-line mx-1" aria-hidden="true" />
          )}
          {years.map((y) => {
            const active = selection.kind === "year" && selection.year === y;
            return (
              <button
                key={y}
                type="button"
                onClick={() => {
                  setSelection({ kind: "year", year: y });
                  trackEvent("metagame_window_change", {
                    kind: "year",
                    value: y,
                  });
                }}
                className={[
                  "px-4 py-2 rounded-lg text-sm font-semibold transition-colors",
                  active
                    ? "bg-brand-700 text-white"
                    : "text-[#6b6356] hover:bg-[#f3efe6]",
                ].join(" ")}
              >
                {y}
              </button>
            );
          })}
        </div>
      </div>

      <p className="max-w-[640px] text-[15px] leading-relaxed text-[#6b6356]">
        Decks mais jogados com a fatia do metagame e o winrate contra os{" "}
        <em>outros</em> arquétipos (espelho excluído). Empates contam como
        derrota.
      </p>

      {metagameQuery.isLoading && <Loading />}
      {metagameQuery.error && <ErrorBox error={metagameQuery.error} />}

      {data && (
        <>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-[13px] text-[#8a8170] font-semibold">
            <span>{summary}</span>
            <span>
              {data.totalDecks} deck{data.totalDecks === 1 ? "" : "s"}{" "}
              classificado{data.totalDecks === 1 ? "" : "s"}
            </span>
            <span>
              {data.totalNonMirrorMatches} partida
              {data.totalNonMirrorMatches === 1 ? "" : "s"} (não-espelho)
            </span>
            <span className="text-[11px] uppercase tracking-wide text-[#a39a87]">
              {data.from} → {data.to}
            </span>
          </div>

          {data.archetypes.length === 0 ? (
            <p className="text-slate-500">
              Sem decks classificados nesse período.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
              {data.archetypes.map((row) => (
                <ArchetypeCard key={row.subarchetypeId} row={row} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ArchetypeCard({ row }: { row: MetagameRow }) {
  const banner = row.representativeCard?.imageArtCropUrl ?? null;
  return (
    <article className="flex flex-col bg-white border border-line rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <Link
        to={`/subarchetypes/${row.subarchetypeId}`}
        className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-700/60"
        aria-label={`Ver detalhes de ${row.subarchetypeName}`}
      >
        <div className="aspect-[16/9] bg-[#efece6] overflow-hidden">
          {banner ? (
            <img
              src={banner}
              alt=""
              loading="lazy"
              decoding="async"
              className="block w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-[repeating-linear-gradient(45deg,#efece6_0_11px,#f3efe6_11px_22px)]">
              <span className="font-mono text-[11px] text-[#9a9182] uppercase tracking-wide">
                sem arte
              </span>
            </div>
          )}
        </div>
      </Link>

      <div className="p-4 flex flex-col gap-2.5 min-w-0">
        <div className="min-w-0">
          <Link
            to={`/subarchetypes/${row.subarchetypeId}`}
            className="font-display font-bold text-[16px] leading-tight text-brand-700 hover:underline truncate block"
          >
            {row.subarchetypeName}
          </Link>
          <span className="inline-block rounded-full bg-brand-50 text-brand-900 text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 mt-1">
            {row.archetypeName}
          </span>
        </div>

        <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 mt-1">
          <div className="flex flex-col">
            <dt className="text-[10px] uppercase tracking-wide text-[#8a8170] font-bold">
              Meta %
            </dt>
            <dd className="font-display font-bold text-[18px] text-ink tabular-nums">
              {formatPct(row.metaShare)}
              <span className="ml-1 text-[12px] font-semibold text-[#9a9182]">
                ({row.decks})
              </span>
            </dd>
          </div>
          <div className="flex flex-col">
            <dt className="text-[10px] uppercase tracking-wide text-[#8a8170] font-bold">
              Winrate
            </dt>
            <dd
              className="font-display font-bold text-[18px] text-ink tabular-nums"
              title={`Empates contam como derrota (${row.wins}-${row.losses}-${row.draws})`}
            >
              {row.nonMirrorMatches > 0 ? formatPct(row.winrate) : "—"}
              <span className="ml-1 text-[12px] font-semibold text-[#9a9182] tabular-nums">
                ({row.wins}–{row.losses}
                {row.draws > 0 ? `–${row.draws}` : ""})
              </span>
            </dd>
          </div>
        </dl>
      </div>
    </article>
  );
}
